import React, { useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQuery } from 'hooks/apollo';
import { gql } from '@apollo/client';

import { Empty, Select } from 'antd';
import ErrorBoundary from 'components/ErrorBoundary';

import messages from '../../messages';
import ResultContent from '../ResultContent';

import './question-comparison.less';

const GET_COMPARE_QUESTIONS = gql`
  query($surveyId: ID!) {
    comparisonQuestions(surveyId: $surveyId) {
      id
      title
      category
    }
  }
`;

const { Option, OptGroup } = Select;

const QuestionComparison = ({ question, filterSegments, dateRange, surveyGroupId, displayedSurveyId, dataSourceId, addToReport, onSelectChartSegment }) => {
  const [comparisonQuestionId, setComparisonQuestion] = useState(null);
  const { loading: compareLoading, data: compareData } = useQuery(GET_COMPARE_QUESTIONS, {
    variables: { surveyId: displayedSurveyId },
  });

  const compareQuestionsGroupedByCategory = useMemo(() => {
    if (!compareData) {
      return [];
    }
    const { comparisonQuestions } = compareData;
    // ["NPS", "Find", "Grade"]
    const uniqueCategories = [...new Set(comparisonQuestions.map(q => q.category))];

    const groupedByCategory = uniqueCategories.map((cat) => {
      const categoryQuestions = comparisonQuestions.filter(q => q.category === cat);
      return {
        label: cat,
        questions: categoryQuestions,
      };
    });

    return groupedByCategory;
  }, [compareData]);

  const compareQuestionChanged = (value) => {
    setComparisonQuestion(value);
  };

  const contentSettings = useMemo(() => ({
    questionIds: question.questionIds,
    comparisonQuestionIds: [comparisonQuestionId],
  }), [question.questionIds, comparisonQuestionId]);

  return (
    <div className="question-comparison">
      <div className="question-comparison__header">
        <FormattedMessage
          {...messages.questionComparisonHeader}
          values={{ question: question.title }}
        />
        <Select
          className="compare-to"
          onChange={compareQuestionChanged}
          loading={compareLoading}
          disabled={compareLoading}
          placeholder={<FormattedMessage {...messages.selectComparison} />}
        >
          {compareData && compareQuestionsGroupedByCategory.map(group => (
            <OptGroup key={group.label} label={messages[group.label.toLowerCase()] ? <FormattedMessage {...messages[group.label.toLowerCase()]} /> : group.label}>
              {group.questions.map(q => <Option key={q.id} value={q.id}><span dangerouslySetInnerHTML={{ __html: q.title }} /></Option>)}
            </OptGroup>
          ))}
        </Select>
      </div>
      {comparisonQuestionId ? (
        <ErrorBoundary>
          <ResultContent
            wrapperClass=""
            hideTitle
            dateRange={dateRange}
            surveyGroupId={surveyGroupId}
            displayedSurveyId={displayedSurveyId}
            filterSegments={filterSegments}
            contentSettings={contentSettings}
            addToReport={addToReport}
            onSelectChartSegment={onSelectChartSegment}
            dataSourceId={dataSourceId}
          />
        </ErrorBoundary>
      ) : (
        <Empty
          className="question-comparison--empty"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<FormattedMessage {...messages.selectComparison} />}
        />
      )}
    </div>
  );
};

export default QuestionComparison;
